/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

/**
 * DOCS_BASE_URL is injected at build time via angular.json `define`.
 * Default is '' (empty string), which hides the help icon entirely.
 * To enable locally: ng serve uses http://localhost:4150/docs by default.
 * devdo: ng build --configuration=devdo uses https://docs.ouromc.ie/docs.
 * production: ng build --configuration=production overrides to '' — icon hidden.
 */
declare const DOCS_BASE_URL: string;

const NEVER_GLOW_KEY = 'help-glow-never';
const SEEN_PREFIX = 'help-seen-';

@Component({
    selector: 'app-doc-help',
    standalone: true,
    imports: [MatIconModule, MatButtonModule, MatTooltipModule, MatMenuModule],
    styles: [`
        @keyframes help-pulse {
            0%   { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.55); }
            65%  { box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
            100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
        }
        .help-unseen {
            animation: help-pulse 2s ease-in-out infinite;
            border-radius: 50%;
        }
    `],
    template: `
        @if (helpUrl) {
            <span style="position:relative; display:inline-block">
                <a [href]="helpUrl"
                   target="_blank"
                   rel="noopener noreferrer"
                   mat-icon-button
                   color="primary"
                   [class.help-unseen]="!seen"
                   (click)="markSeen()"
                   (contextmenu)="onRightClick($event)"
                   matTooltip="Open help documentation"
                   i18n-matTooltip>
                    <mat-icon>help_outline</mat-icon>
                </a>
                <span style="visibility:hidden; position:fixed; pointer-events:none"
                      [style.left]="menuX"
                      [style.top]="menuY"
                      [matMenuTriggerFor]="contextMenu"
                      #menuTrigger="matMenuTrigger">
                </span>
            </span>
            <mat-menu #contextMenu>
                <button mat-menu-item (click)="disableGlow()" [disabled]="glowDisabled">
                    <mat-icon>notifications_off</mat-icon>
                    <span i18n>Never highlight unread help</span>
                </button>
                <button mat-menu-item (click)="enableGlow()" [disabled]="!glowDisabled">
                    <mat-icon>notifications_active</mat-icon>
                    <span i18n>Highlight unread help</span>
                </button>
                <button mat-menu-item (click)="clearAllMarkers()">
                    <mat-icon>restart_alt</mat-icon>
                    <span i18n>Clear all read markers</span>
                </button>
            </mat-menu>
        }
    `
})
export class DocHelpComponent implements OnChanges {
    @Input() docPath?: string | null;
    @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;

    seen = true;         // optimistic — avoids flash before ngOnChanges fires
    glowDisabled = false; // tracks whether NEVER_GLOW_KEY is set
    menuX = '0px';
    menuY = '0px';

    get helpUrl(): string | null {
        const base = DOCS_BASE_URL;
        if (!base || !this.docPath) return null;
        return `${base}/${this.docPath}`;
    }

    ngOnChanges(): void {
        this.glowDisabled = !!localStorage.getItem(NEVER_GLOW_KEY);
        if (this.docPath) {
            this.seen = this.glowDisabled
                     || !!localStorage.getItem(`${SEEN_PREFIX}${this.docPath}`);
        } else {
            this.seen = true;
        }
    }

    markSeen(): void {
        if (this.docPath) {
            localStorage.setItem(`${SEEN_PREFIX}${this.docPath}`, '1');
            this.seen = true;
        }
    }

    onRightClick(event: MouseEvent): void {
        event.preventDefault();
        this.menuX = event.clientX + 'px';
        this.menuY = event.clientY + 'px';
        // one tick so the style binding updates before CDK reads the position
        setTimeout(() => this.menuTrigger?.openMenu(), 0);
    }

    disableGlow(): void {
        localStorage.setItem(NEVER_GLOW_KEY, '1');
        this.glowDisabled = true;
        this.seen = true;
    }

    enableGlow(): void {
        localStorage.removeItem(NEVER_GLOW_KEY);
        this.glowDisabled = false;
        this.seen = this.docPath
            ? !!localStorage.getItem(`${SEEN_PREFIX}${this.docPath}`)
            : true;
    }

    clearAllMarkers(): void {
        Object.keys(localStorage)
            .filter(k => k.startsWith(SEEN_PREFIX))
            .forEach(k => localStorage.removeItem(k));
        // Re-glow the current page if glow is enabled
        if (!this.glowDisabled) {
            this.seen = false;
        }
    }
}
